// src/components/PropertyView/PropertyDetailsCard.js
// Configuración de secciones y funciones helper para renderizado de metadata JSONB
// ✅ REFACTORIZADO: Separado de PropertyDetailsTabs para mejor mantenibilidad

import React from 'react';
import {
  Bed, Bath, Maximize, Building, MapPin, Home, Package, Car,
  Wrench, Zap, Shield, DollarSign, Users, FileCheck,
  Calendar, TrendingUp, FileText, Wind, Droplets
} from 'lucide-react';

/**
 * CONFIGURACIÓN DE SECCIONES Y CAMPOS
 * Estructura de 9 secciones con 41 campos totales
 */
export const sectionsConfig = {
  // SECCIÓN 1: CARACTERÍSTICAS FÍSICAS (11 campos)
  physical: {
    title: 'Características Físicas',
    titleKey: 'details.sections.physical',
    icon: Home,
    color: 'blue',
    fields: [
      { key: 'bedrooms', label: 'Habitaciones', labelKey: 'details.fields.bedrooms', icon: Bed, color: 'blue-400' },
      { key: 'bathrooms', label: 'Baños', labelKey: 'details.fields.bathrooms', icon: Bath, color: 'cyan-400' },
      { key: 'builtArea', label: 'Superficie construida', labelKey: 'details.fields.builtArea', icon: Maximize, color: 'purple-400', suffix: ' m²' },
      { key: 'usableArea', label: 'Superficie útil', labelKey: 'details.fields.usableArea', icon: Maximize, color: 'purple-400', suffix: ' m²' },
      { key: 'ceilingHeight', label: 'Altura techos', labelKey: 'details.fields.ceilingHeight', icon: TrendingUp, color: 'indigo-400', suffix: ' m' },
      { key: 'orientation', label: 'Orientación', labelKey: 'details.fields.orientation', icon: MapPin, color: 'orange-400' },
      { key: 'exteriorInterior', label: 'Exterior/Interior', labelKey: 'details.fields.exteriorInterior', icon: Building, color: 'slate-400' },
      { key: 'outdoorSpaces', label: 'Zonas exteriores', labelKey: 'details.fields.outdoorSpaces', icon: Wind, color: 'green-400' },
      { key: 'laundry', label: 'Lavandería', labelKey: 'details.fields.laundry', icon: Droplets, color: 'cyan-400' },
      { key: 'furnished', label: 'Amueblado', labelKey: 'details.fields.furnished', icon: Home, color: 'amber-400' },
      { key: 'builtInWardrobes', label: 'Armarios empotrados', labelKey: 'details.fields.builtInWardrobes', icon: Package, color: 'slate-400', isBoolean: true }
    ]
  },

  // SECCIÓN 2: EDIFICIO (7 campos)
  building: {
    title: 'Edificio',
    titleKey: 'details.sections.building',
    icon: Building,
    color: 'indigo',
    fields: [
      { key: 'elevator', label: 'Ascensor', labelKey: 'details.fields.elevator', icon: Building, color: 'indigo-400', isBoolean: true },
      { key: 'floor', label: 'Planta', labelKey: 'details.fields.floor', icon: Building, color: 'indigo-400' },
      { key: 'topFloor', label: 'Última planta', labelKey: 'details.fields.topFloor', icon: TrendingUp, color: 'purple-400', isBoolean: true },
      { key: 'totalFloors', label: 'Total plantas edificio', labelKey: 'details.fields.totalFloors', icon: Building, color: 'slate-400' },
      { key: 'constructionYear', label: 'Año construcción', labelKey: 'details.fields.constructionYear', icon: Calendar, color: 'slate-400' },
      { key: 'iteStatus', label: 'Estado ITE', labelKey: 'details.fields.iteStatus', icon: FileCheck, color: 'emerald-400' },
      { key: 'accessibility', label: 'Accesibilidad', labelKey: 'details.fields.accessibility', icon: Users, color: 'blue-400' }
    ]
  },

  // SECCIÓN 3: ESPACIOS ASOCIADOS (2 campos)
  associatedSpaces: {
    title: 'Espacios Asociados',
    titleKey: 'details.sections.associatedSpaces',
    icon: Package,
    color: 'green',
    fields: [
      { key: 'parkingSpaces', label: 'Parking', labelKey: 'details.fields.parkingSpaces', icon: Car, color: 'green-400' },
      { key: 'storageRoom', label: 'Trastero', labelKey: 'details.fields.storageRoom', icon: Package, color: 'slate-400' }
    ]
  },

  // SECCIÓN 4: ESTADO Y ACABADOS (3 campos)
  condition: {
    title: 'Estado y Acabados',
    titleKey: 'details.sections.condition',
    icon: Wrench,
    color: 'orange',
    fields: [
      { key: 'condition', label: 'Estado general', labelKey: 'details.fields.condition', icon: Wrench, color: 'orange-400' },
      { key: 'kitchen', label: 'Cocina', labelKey: 'details.fields.kitchen', icon: Home, color: 'amber-400' },
      { key: 'windows', label: 'Carpintería exterior', labelKey: 'details.fields.windows', icon: Building, color: 'slate-400' }
    ]
  },

  // SECCIÓN 5: INSTALACIONES (4 campos)
  installations: {
    title: 'Instalaciones',
    titleKey: 'details.sections.installations',
    icon: Zap,
    color: 'purple',
    fields: [
      { key: 'heating', label: 'Calefacción', labelKey: 'details.fields.heating', icon: Zap, color: 'red-400' },
      { key: 'airConditioning', label: 'Aire acondicionado', labelKey: 'details.fields.airConditioning', icon: Wind, color: 'cyan-400' },
      { key: 'hotWater', label: 'Agua caliente', labelKey: 'details.fields.hotWater', icon: Droplets, color: 'blue-400' },
      { key: 'energyRating', label: 'Certificación energética', labelKey: 'details.fields.energyRating', icon: FileText, color: 'green-400' }
    ]
  },

  // SECCIÓN 6: SEGURIDAD (3 campos)
  security: {
    title: 'Seguridad',
    titleKey: 'details.sections.security',
    icon: Shield,
    color: 'cyan',
    fields: [
      { key: 'securityDoor', label: 'Puerta blindada/acorazada', labelKey: 'details.fields.securityDoor', icon: Shield, color: 'cyan-400', isBoolean: true },
      { key: 'videoIntercom', label: 'Videoportero', labelKey: 'details.fields.videoIntercom', icon: FileText, color: 'slate-400', isBoolean: true },
      { key: 'alarm', label: 'Alarma', labelKey: 'details.fields.alarm', icon: Shield, color: 'red-400', isBoolean: true }
    ]
  },

  // SECCIÓN 7: COSTES Y VALORACIÓN (5 campos)
  costs: {
    title: 'Costes y Valoración',
    titleKey: 'details.sections.costs',
    icon: DollarSign,
    color: 'amber',
    fields: [
      { key: 'ibiTax', label: 'IBI anual', labelKey: 'details.fields.ibiTax', icon: DollarSign, color: 'amber-400', suffix: ' €/año' },
      { key: 'communityFees', label: 'Gastos comunidad', labelKey: 'details.fields.communityFees', icon: DollarSign, color: 'yellow-400', suffix: ' €/mes' },
      { key: 'pendingAssessments', label: 'Derramas pendientes', labelKey: 'details.fields.pendingAssessments', icon: DollarSign, color: 'red-400' },
      { key: 'cadastralValue', label: 'Valor catastral', labelKey: 'details.fields.cadastralValue', icon: TrendingUp, color: 'emerald-400', suffix: ' €' },
      { key: 'appraisal', label: 'Tasación', labelKey: 'details.fields.appraisal', icon: TrendingUp, color: 'emerald-400', suffix: ' €' }
    ]
  },

  // SECCIÓN 8: COMUNIDAD (2 campos)
  community: {
    title: 'Comunidad',
    titleKey: 'details.sections.community',
    icon: Users,
    color: 'slate',
    fields: [
      { key: 'commonAreasRenovation', label: 'Reforma zonas comunes', labelKey: 'details.fields.commonAreasRenovation', icon: Wrench, color: 'orange-400' },
      { key: 'concierge', label: 'Portero/Conserje', labelKey: 'details.fields.concierge', icon: Users, color: 'slate-400', isBoolean: true }
    ]
  },

  // SECCIÓN 9: SITUACIÓN LEGAL (4 campos)
  legal: {
    title: 'Situación Legal',
    titleKey: 'details.sections.legal',
    icon: FileCheck,
    color: 'red',
    fields: [
      { key: 'encumbrances', label: 'Cargas', labelKey: 'details.fields.encumbrances', icon: FileCheck, color: 'red-400' },
      { key: 'habitabilityCertificate', label: 'Cédula habitabilidad', labelKey: 'details.fields.habitabilityCertificate', icon: FileText, color: 'green-400' },
      { key: 'tenantStatus', label: 'Situación inquilino', labelKey: 'details.fields.tenantStatus', icon: Users, color: 'slate-400' },
      { key: 'areaRentalPrice', label: 'Precio alquiler zona', labelKey: 'details.fields.areaRentalPrice', icon: DollarSign, color: 'amber-400', suffix: ' €/mes' }
    ]
  }
};

/**
 * HELPER: Renderizar un item individual de metadata
 * @param {object} item - Configuración del campo
 * @param {object} metadata - Datos de la propiedad
 * @param {function} t - Función de traducción de i18next
 */
export const renderMetadataItem = (item, metadata, t, translations) => {
  const value = metadata[item.key];

  // No mostrar si no hay valor
  if (value === undefined || value === null || value === '') return null;

  // Formatear valor según tipo
  let displayValue = value;
  if (item.isBoolean) {
    displayValue = value ? t('common.yes') : t('common.no');
  } else if (typeof value === 'number') {
    displayValue = value.toLocaleString();
  } else if (translations?.[`metadata.${item.key}`]) {
    displayValue = translations[`metadata.${item.key}`];
  }

  // Añadir sufijo si existe
  if (item.suffix) {
    displayValue = `${displayValue}${item.suffix}`;
  }

  const IconComponent = item.icon;
  const label = t ? t(item.labelKey) : item.label;

  return (
    <div key={item.key} className="flex items-center justify-between text-sm p-2 bg-slate-900/20 rounded">
      <div className="flex items-center gap-2 text-slate-400">
        <IconComponent className={`w-4 h-4 text-${item.color}`} />
        <span>{label}</span>
      </div>
      <span className="font-semibold text-white">{displayValue}</span>
    </div>
  );
};

/**
 * HELPER: Renderizar una sección completa
 * @param {string} sectionKey - Clave de la sección
 * @param {object} sectionConfig - Configuración de la sección
 * @param {object} metadata - Datos de la propiedad
 * @param {function} t - Función de traducción de i18next
 */
export const renderSection = (sectionKey, sectionConfig, metadata, t, translations) => {
  const items = sectionConfig.fields
    .map(field => renderMetadataItem(field, metadata, t, translations))
    .filter(item => item !== null);

  // No mostrar sección si no tiene items
  if (items.length === 0) return null;

  const SectionIcon = sectionConfig.icon;
  const sectionTitle = (t && sectionConfig.titleKey) ? t(sectionConfig.titleKey) : sectionConfig.title;

  return (
    <div key={sectionKey} className="mb-4">
      <div className="flex items-center gap-2 mb-3">
        <SectionIcon className={`w-5 h-5 text-${sectionConfig.color}-400`} />
        <h3 className="text-sm font-semibold text-slate-300">{sectionTitle}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items}
      </div>
    </div>
  );
};

/**
 * HELPER: Renderizar campos personalizados (customFields)
 */
export const renderCustomFields = (metadata, translations) => {
  if (!metadata.customFields || Object.keys(metadata.customFields).length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {Object.entries(metadata.customFields).map(([key, value]) => {
        const displayKey = translations?.[`customField.key.${key}`] ?? key;
        const displayValue = translations?.[`customField.value.${key}`] ?? value;
        return (
          <div key={key} className="flex items-center justify-between text-sm p-2 bg-slate-900/20 rounded">
            <span className="text-slate-400">{displayKey}</span>
            <span className="font-medium text-slate-300 text-right ml-4">{displayValue}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * HELPER: Verificar si una sección tiene datos
 */
export const hasSectionData = (sectionConfig, metadata) => {
  return sectionConfig.fields.some(field => {
    const value = metadata[field.key];
    return value !== undefined && value !== null && value !== '';
  });
};
